import type { HTMLAttributes, ReactNode } from 'react';

/* ────────────────────────────────────── EditorShell.Pane
 *
 * Non-scrolling positioning context that wraps the scrolling Stage and hosts
 * the bottom-pinned FormattingToolbar. It is the flex sibling of the chat rail
 * (it carries the stage's old `flex:1`), so its box width is the editor column
 * width and EXCLUDES the chat rail. Because the Pane (not the scrolling Stage)
 * is `position: relative`, the absolutely-positioned toolbar anchors to the
 * Pane's bottom and DOES NOT move when the Stage scrolls beneath it. This is
 * the fix for the "toolbar scrolls with content" regression: the toolbar must
 * never be a child whose containing block scrolls.
 */

export interface EditorShellPaneProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function EditorShellPane({ children, className, ...rest }: EditorShellPaneProps) {
  return (
    <div
      className={['pn-editor-shell-pane', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ────────────────────────────────────── EditorShell.Stage
 *
 * Scrolling, centered column that hosts the editor canvas. Use `<main>` so
 * assistive tech treats the editor surface as the page's primary landmark.
 * The Stage scrolls (`overflow-y:auto`); the bottom-pinned FormattingToolbar
 * lives on the non-scrolling `EditorShell.Pane` sibling, NOT inside the Stage,
 * so it stays fixed while content scrolls.
 */

export interface EditorShellStageProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

function EditorShellStage({ children, className, ...rest }: EditorShellStageProps) {
  return (
    <main
      className={['pn-editor-shell-stage', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </main>
  );
}

/* ────────────────────────────────────── EditorShell.Canvas
 *
 * The 720px-max content column inside the stage. Holds the crumb, title, body,
 * and any below-body sections the page wants stacked with the editor (e.g.
 * WorkItemDetail's SubtasksChecklist + ChildTasksPanel).
 */

export interface EditorShellCanvasProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

function EditorShellCanvas({ children, className, ...rest }: EditorShellCanvasProps) {
  return (
    <article
      className={['pn-editor-shell-canvas', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </article>
  );
}

/* ────────────────────────────────────── EditorShell
 *
 * Workspace root for the V2 Notion-canvas relayout. A `position: relative`
 * flex container that fills its parent and clips its overflow. Mount `Pane`
 * (which wraps the scrolling `Stage` + the bottom-pinned FormattingToolbar)
 * as the flex-1 body column; mount overlays (e.g. `InspectorTabs`) and the
 * chat rail as sibling children so they anchor to the shell rather than
 * scrolling with the stage's content.
 */

export interface EditorShellProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function EditorShell({ children, className, ...rest }: EditorShellProps) {
  return (
    <div
      className={['pn-editor-shell', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

EditorShell.Pane = EditorShellPane;
EditorShell.Stage = EditorShellStage;
EditorShell.Canvas = EditorShellCanvas;
